import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Handshake, TrendingUp, Home, ChevronRight } from "lucide-react";

const M = process.env.REACT_APP_BACKEND_URL;
const SHOT = (n) => `${M}/api/media/assets/rejoindre/${n}.jpg?v=2`;

// 3 univers — uniquement des fonctionnalités RÉELLES de l'espace pro (vraies captures mobiles PROD).
const COLUMNS = [
  {
    id: "developper", num: "01", title: "DÉVELOPPER", icon: TrendingUp,
    tagline: "Une prospection augmentée par l'IA",
    desc: "Votre Agent IA analyse votre portefeuille, recherche et qualifie des acquéreurs en continu — puis remonte les meilleures correspondances, même quand vous ne prospectez pas.",
    shots: [
      { img: "dev_emma", label: "Agent IA de prospection", sub: "Il travaille même quand vous dormez" },
      { img: "dev_prospects", label: "Votre bilan du matin", sub: "Prospects chauds, matchs, radar multicanal" },
      { img: "dev_matching", label: "Matching IA", sub: "Agents recommandés par valeur réelle" },
    ],
  },
  {
    id: "vendre", num: "02", title: "VENDRE", icon: Home,
    tagline: "Des biens mis en valeur, vendus plus vite",
    desc: "Créez des annonces premium en un lien, révélez le potentiel des biens grâce au home staging IA sur la vraie photo, et diffusez le tout sur votre propre site connecté au réseau.",
    shots: [
      { img: "vendre_annonces", label: "Créer & gérer vos annonces", sub: "Import en un lien, IA rédactionnelle" },
      { img: "vendre_staging", label: "Home staging IA", sub: "Avant / après sur la vraie photo" },
      { img: "vendre_multilang", label: "Site & annonces multilingues", sub: "Présentés en 6 langues (FR·EN·ES·DE·PT·中文)" },
    ],
  },
  {
    id: "collaborer", num: "03", title: "COLLABORER", icon: Handshake,
    tagline: "La concurrence transformée en collaboration",
    desc: "Partagez vos opportunités avec les partenaires que vous choisissez, co-mandatez en toute transparence et connectez vos acquéreurs au bon bien — l'origine reste toujours tracée.",
    shots: [
      { img: "collab_reseau", label: "Réseau d'agents & apports", sub: "Partenaires, apports reçus et transmis" },
      { img: "collab_comandat", label: "Co-mandats & rétrocommissions", sub: "75/25, 50/50 — validés des deux côtés" },
      { img: "collab_acquereur", label: "Espace acquéreur", sub: "Recherche vocale intelligente" },
    ],
  },
];

function PhoneShot({ shot, delay = 0 }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay }}
      className="snap-center shrink-0 w-[74%] xs:w-[62%] sm:w-[46%] md:w-auto"
      data-testid={`ecosystem-shot-${shot.img}`}
    >
      <div className="relative mx-auto max-w-[230px] rounded-[2rem] p-[5px] bg-gradient-to-b from-[#26262c] to-[#101014] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
        <div className="rounded-[1.6rem] overflow-hidden bg-black">
          <img src={SHOT(shot.img)} alt={shot.label} loading="lazy" className="w-full block select-none pointer-events-none" draggable="false" />
        </div>
      </div>
      <figcaption className="mt-4 text-center px-2">
        <div className="text-[13.5px] font-medium text-white">{shot.label}</div>
        <div className="text-[11.5px] text-zinc-500 mt-0.5 leading-snug">{shot.sub}</div>
      </figcaption>
    </motion.figure>
  );
}

export default function Ecosystem() {
  const [active, setActive] = useState("developper");
  const current = COLUMNS.find((c) => c.id === active) || COLUMNS[0];

  return (
    <section className="relative bg-[#0d0d10] text-white py-20 sm:py-28 overflow-hidden" data-testid="home-ecosystem">
      <div aria-hidden className="pointer-events-none absolute top-24 -left-32 w-[34rem] h-[34rem] rounded-full bg-[#C19A6B]/[0.07] blur-[130px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* En-tête */}
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C19A6B] font-semibold mb-4">Un réseau augmenté par l'IA</p>
          <h2 aria-label="Un écosystème immobilier nouvelle génération" className="calli text-4xl sm:text-5xl leading-tight" data-testid="ecosystem-title">
            Un écosystème immobilier <span className="text-[#C19A6B]">nouvelle génération</span>
          </h2>
          <p className="text-base text-zinc-300 mt-6 leading-relaxed">
            Trois façons d'avancer avec Off Markette : <span className="text-white font-medium">développer</span> votre activité, <span className="text-white font-medium">vendre</span> mieux et <span className="text-white font-medium">collaborer</span> intelligemment. Chaque professionnel garde son réseau, ses clients et son indépendance — pendant que nos Agents IA analysent, recherchent et préparent en continu.
          </p>
        </div>

        {/* Sélecteur 3 colonnes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-12" data-testid="ecosystem-columns">
          {COLUMNS.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id} type="button" onClick={() => setActive(c.id)} data-testid={`ecosystem-col-${c.id}`}
                aria-expanded={isActive}
                className={`group text-left rounded-2xl border p-5 sm:p-6 transition-all duration-300 ${isActive ? "border-[#C19A6B]/60 bg-[#C19A6B]/[0.08] shadow-[0_0_40px_-12px_rgba(193,154,107,0.35)]" : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${isActive ? "bg-[#C19A6B] text-black" : "bg-white/5 text-[#C19A6B] group-hover:bg-white/10"}`}>
                    <c.icon className="w-5 h-5" />
                  </span>
                  <span className={`text-[11px] font-mono tracking-widest ${isActive ? "text-[#C19A6B]" : "text-zinc-600"}`}>{c.num}</span>
                  <ChevronRight className={`ml-auto w-5 h-5 transition-transform duration-300 ${isActive ? "text-[#C19A6B] rotate-90" : "text-zinc-600 group-hover:translate-x-1"}`} />
                </div>
                <h3 className="mt-4 text-xl font-semibold tracking-wide text-white">{c.title}</h3>
                <p className={`mt-1 text-[13px] leading-snug ${isActive ? "text-[#C19A6B]" : "text-zinc-400"}`}>{c.tagline}</p>
              </button>
            );
          })}
        </div>

        {/* Panneau actif — vraies captures de l'espace pro */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}
            className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-b from-[#111116] to-[#0b0b0e] p-6 sm:p-9"
            data-testid={`ecosystem-panel-${active}`}
          >
            <p className="max-w-2xl text-[15px] text-zinc-300 leading-relaxed">{current.desc}</p>
            <div className="mt-8 flex md:grid md:grid-cols-3 gap-5 sm:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-2 -mx-2 px-2 scrollbar-hide">
              {current.shots.map((s, i) => (
                <PhoneShot key={s.img} shot={s} delay={i * 0.08} />
              ))}
            </div>
            <p className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-zinc-600">Captures réelles de l'espace professionnel Off Markette</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
